import { Popover } from '@radix-ui/react-popover';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizeObserver } from 'usehooks-ts';

import {
  useArrowKeyNavigation,
  useControlledState,
  useOnClickOutside,
} from '../../hooks';
import { useTheme } from '../../theme';
import { getPxValue } from '../../utils';
import EmphasizedText from '../EmphasizedText/EmphasizedText';
import { InputIcon } from '../Input';
import List from '../List';
import Spinner from '../Spinner/Spinner';

import {
  ArrowIcon,
  DropdownListItem,
  DropdownListItemCheckbox,
  DropdownPopoverContent,
  DropdownRoot,
  DropdownSearchInput,
  IconContainer,
  NoResults,
  SpinnerContainer,
} from './Dropdown.styled';
import DropdownInput from './DropdownInput';
import { DropdownOption, DropdownProps } from './types';
import useFilteredOptions from './useFilteredOptions';

const renderOption = (option: DropdownOption, children: ReactNode) => {
  const { renderLabel, icon } = option;

  const content = renderLabel?.() || children;

  if (!icon) {
    return content;
  }

  if (typeof icon === 'string') {
    return <IconContainer $src={icon}>{content}</IconContainer>;
  }

  return (
    <IconContainer>
      {icon}
      {content}
    </IconContainer>
  );
};

const Dropdown = ({
  'data-testid': dataTestId,
  isDisabled,
  isLoading,
  className,
  options = [],
  inputProps,
  value: selectedValueProp,
  selectedOption: selectedOptionProp,
  onChange,
  onFilterChange,
  isMultiselect,
  isRequired,
  isSearchable,
  popoverSide = 'bottom',
}: DropdownProps) => {
  const theme = useTheme();
  const [isOpen, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const isSearchActive = useRef(false);
  const focusedOption = useRef<HTMLLIElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedValue =
    typeof selectedValueProp === 'undefined'
      ? selectedOptionProp?.value
      : selectedValueProp;

  const selectedOption = useMemo(
    () =>
      selectedOptionProp ||
      options.find(option => option.value === selectedValue),
    [options, selectedValue, selectedOptionProp],
  );

  const { width: dropdownWidth } = useResizeObserver({ ref: dropdownRef });

  const initialInputValue = !isMultiselect ? selectedOption?.label : '';

  const [inputValue, setInputValue] = useControlledState(
    initialInputValue || '',
  );

  const [searchTerm, setSearchTerm] = useState('');

  const [filteredOptions, setFilter] = useFilteredOptions({
    options,
    onChange: onFilterChange,
  });

  const setPreviousSelectedOption = () => {
    if (isDisabled) return;

    if (!isMultiselect && selectedOption) {
      setInputValue(selectedOption.label);
    }

    setOpen(false);
  };

  useOnClickOutside(dropdownRef, setPreviousSelectedOption);

  const { current } = useArrowKeyNavigation<DropdownOption>({
    element: dropdownRef.current,
    isDisabled: !isOpen,
    items: filteredOptions,
  });

  const handleMultiselectInput = useCallback(
    (newValue: string[]) => {
      if (newValue.length === 0) setInputValue('');
      else {
        setInputValue(
          newValue.length > 1
            ? `${newValue.length} selected`
            : options.find(item => item.value === newValue[0])?.label || '',
        );
      }
    },
    [options, setInputValue],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isMultiselect) return;

    const targetValue = event.target.value;

    isSearchActive.current = true;

    setInputValue(targetValue);

    setOpen(true);

    setFilter(targetValue);
  };

  const handleOptionSelect = useCallback(
    (option: DropdownOption) => {
      isSearchActive.current = false;

      if (!isMultiselect) {
        setInputValue(option.label);
        setOpen(false);
        onChange(option.value);
      } else {
        const valueArray = Array.isArray(selectedValue) ? selectedValue : [];

        const newValue = valueArray.includes(option.value)
          ? valueArray.filter(item => item !== option.value)
          : [...valueArray, option.value];

        if (isRequired && newValue.length === 0) return;

        onChange(newValue);
        handleMultiselectInput(newValue);

        searchInputRef.current?.select();
      }
    },
    [
      isMultiselect,
      setInputValue,
      onChange,
      selectedValue,
      isRequired,
      handleMultiselectInput,
    ],
  );

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    inputProps.onFocus?.(event);

    if (event.isDefaultPrevented()) return;

    setOpen(true);
    if (!isMultiselect) {
      event.target.select();
      setFilter('');
    }
  };

  const handleDropdownKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === 'Enter' && current) {
      handleOptionSelect(current);

      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      if (isOpen) {
        event.stopPropagation();
      }

      return;
    }

    if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setOpen(true);

      if (focusedOption.current) {
        optionsContainerRef.current?.scrollTo(
          0,
          focusedOption.current?.offsetTop,
        );
      }
    }
  };

  const handleDropdownBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      event.relatedTarget &&
      !dropdownRef.current?.contains(event.relatedTarget)
    ) {
      setPreviousSelectedOption();
      if (isSearchable) {
        setSearchTerm('');
      }
    }
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const targetValue = event.target.value;

    isSearchActive.current = true;

    setSearchTerm(targetValue);

    setFilter(targetValue);
  };

  useEffect(() => {
    if (isMultiselect) {
      setFilter('');
      handleMultiselectInput(Array.isArray(selectedValue) ? selectedValue : []);
    }
  }, [
    handleMultiselectInput,
    isMultiselect,
    options,
    setFilter,
    selectedValue,
  ]);

  useEffect(() => {
    if (isSearchable) {
      isOpen ? searchInputRef.current?.select() : setSearchTerm('');
    }
  }, [isOpen, isSearchable, setSearchTerm]);

  const sideOffset = useMemo(
    () => getPxValue(theme.components.Dropdown.stack),
    [theme.components.Dropdown.stack],
  );

  const getSearchElements = (option: DropdownOption) =>
    isSearchActive.current
      ? renderOption(
          option,
          <EmphasizedText match={inputValue} text={option.label} />,
        )
      : renderOption(option, option.label);

  const isOptionSelected = (optionValue: string) =>
    Array.isArray(selectedValue)
      ? selectedValue.includes(optionValue)
      : optionValue === selectedValue;

  const renderPopoverListItems = () => {
    if (isLoading) {
      return (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      );
    }

    if (!filteredOptions.length) return <NoResults>No results</NoResults>; // todo: make translatable

    return filteredOptions.map(option => (
      <DropdownListItem
        hasTransparentBackground
        $isMultiselect={isMultiselect}
        data-value={option.value}
        isDisabled={option.isDisabled}
        isHighlighted={option === current}
        isSelected={isOptionSelected(option.value)}
        key={option.key || `${option.label}-${option.value}`}
        ref={option === current ? focusedOption : null}
        tabIndex={-1}
        onClick={e => {
          e.preventDefault();
          handleOptionSelect(option);
        }}
        onMouseDown={e => {
          // with this we prevent any potential "outside mousedown" listeners from preventing onClick event
          e.preventDefault();
          e.stopPropagation();
        }}>
        {isMultiselect ? (
          <DropdownListItemCheckbox
            checked={isOptionSelected(option.value)}
            label={renderOption(option, option.label)}
            tabIndex={-1}
            onChange={() => handleOptionSelect(option)}
          />
        ) : (
          getSearchElements(option)
        )}
      </DropdownListItem>
    ));
  };

  const inputIcon = isSearchActive.current ? undefined : selectedOption?.icon;

  return (
    <Popover open={isOpen && !isDisabled}>
      <DropdownRoot
        className={className}
        data-testid={dataTestId}
        ref={dropdownRef}
        onBlur={handleDropdownBlur}
        onClick={() => {
          if (!isOpen && !isDisabled) setOpen(true);
        }}
        onKeyDown={handleDropdownKeyDown}>
        <DropdownInput
          isErrorInitiallyHidden
          autoComplete="off"
          {...inputProps}
          disabled={isDisabled}
          leading={
            typeof inputIcon === 'string' ? (
              <InputIcon src={inputIcon} />
            ) : (
              inputIcon
            )
          }
          readOnly={isMultiselect || inputProps.readOnly}
          trailing={<ArrowIcon $isOpen={isOpen} />}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />

        <DropdownPopoverContent
          aria-expanded={isOpen && !isDisabled}
          collisionPadding={8}
          forceMount={!isDisabled ? true : undefined}
          ref={optionsContainerRef}
          side={popoverSide}
          sideOffset={sideOffset}
          style={{ width: dropdownWidth }}
          onOpenAutoFocus={event => {
            event.preventDefault();
          }}>
          <List data-testid={`${inputProps.label}-options`}>
            {isSearchable && (
              <DropdownSearchInput
                isErrorInitiallyHidden
                isLabelHidden
                label={`${inputProps.label} search`}
                placeholder={`${inputProps.label} search`}
                ref={searchInputRef}
                value={searchTerm}
                onChange={handleSearchInputChange}
              />
            )}
            {renderPopoverListItems()}
          </List>
        </DropdownPopoverContent>
      </DropdownRoot>
    </Popover>
  );
};

export default Dropdown;
